import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { LoadingState } from "./components/ui";
import { AppLayout } from "./layouts/AppLayout";
import { AuthCallbackPage } from "./pages/AuthCallbackPage";
import { CallJourneyPage } from "./pages/CallJourneyPage";
import { CustomerDetailPage } from "./pages/CustomerDetailPage";
import { CustomersPage } from "./pages/CustomersPage";
import { FocusPage } from "./pages/FocusPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PlatformPage } from "./pages/PlatformPage";
import { ReleaseNotesPage } from "./pages/ReleaseNotesPage";
import { DocPage, DocsIndexPage } from "./pages/docs/DocPage";
import { DocsLayout } from "./pages/docs/DocsLayout";
import { TrustedAdvisorPage } from "./pages/TrustedAdvisorPage";
import { AccountPage } from "./pages/AccountPage";
import { AccessPage, AlarmsPage, AuditPage, JourneysPage, LegacyContactRedirect, LegacyTabRedirect, QuotasPage, SupportPage, TrustedAdvisorEnvironmentPage } from "./pages/modules";
import { AliasesPage } from "./pages/flow/AliasesPage";
import { ProfilePage } from "./pages/flow/ProfilePage";
import { ProfilesPage } from "./pages/flow/ProfilesPage";
import { PromotionsPage } from "./pages/flow/PromotionsPage";
import { CachePage } from "./pages/flow/CachePage";
import { EnvironmentGate } from "./components/Environment";
import { moduleById } from "./lib/modules";
import { isLandingPage } from "./lib/preferences";

const LANDED_KEY = "starsach.landed";

export function RequireAuth({ children }: { children: JSX.Element }) {
  const { status, me, preferences } = useAuth();
  const location = useLocation();
  if (status === "loading") return <LoadingState label="Signing in" />;
  if (status === "signed-out") return <Navigate to="/login" state={{ from: location }} replace />;
  // Section 13.1: the chosen landing page, once per tab, when the sign-in arrived at the root rather than a deep link.
  // A page the role can no longer choose falls back to Focus with a one-time notice.
  if (location.pathname === "/" && !sessionStorage.getItem(LANDED_KEY) && !me?.viewAs) {
    sessionStorage.setItem(LANDED_KEY, "1");
    const wanted = preferences.landingPage;
    if (wanted !== "/") {
      return isLandingPage(wanted, me?.role) ? <Navigate to={wanted} replace /> : <Navigate to="/" state={{ landingFallback: wanted }} replace />;
    }
  }
  return children;
}

/** An old address that carried a hash (the footer's version link) keeps it on the way to its new home. */
function KeepHash({ to }: { to: string }) {
  const { hash } = useLocation();
  return <Navigate to={`${to}${hash}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<FocusPage />} />
        {/* Section 8.9: modules are top level and carry their environment in the path. */}
        <Route path="/alarms" element={<AlarmsPage />} />
        <Route path="/alarms/:environmentId" element={<AlarmsPage />} />
        <Route path="/journeys" element={<EnvironmentGate module={moduleById("journeys")} section="overview" />} />
        <Route path="/journeys/:environmentId" element={<Navigate to="overview" replace />} />
        <Route path="/journeys/:environmentId/contacts/:contactId" element={<CallJourneyPage />} />
        <Route path="/journeys/:environmentId/:section" element={<JourneysPage />} />
        <Route path="/flow-configuration" element={<EnvironmentGate module={moduleById("flow-configuration")} section="profiles" />} />
        <Route path="/flow-configuration/:environmentId" element={<Navigate to="profiles" replace />} />
        <Route path="/flow-configuration/:environmentId/profiles" element={<ProfilesPage />} />
        <Route path="/flow-configuration/:environmentId/profiles/:profileId" element={<ProfilePage />} />
        <Route path="/flow-configuration/:environmentId/profiles/:profileId/:tab" element={<ProfilePage />} />
        <Route path="/flow-configuration/:environmentId/aliases" element={<AliasesPage />} />
        {/* Numbers became Aliases (ADR-0020); the old address still lands. */}
        <Route path="/flow-configuration/:environmentId/numbers" element={<Navigate to="../aliases" replace relative="path" />} />
        <Route path="/flow-configuration/:environmentId/promotions" element={<PromotionsPage />} />
        <Route path="/flow-configuration/:environmentId/cache" element={<CachePage />} />
        <Route path="/quotas" element={<QuotasPage />} />
        <Route path="/quotas/:environmentId" element={<QuotasPage />} />
        <Route path="/trusted-advisor" element={<TrustedAdvisorPage />} />
        <Route path="/trusted-advisor/:environmentId" element={<TrustedAdvisorEnvironmentPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/:environmentId" element={<SupportPage />} />
        <Route path="/access" element={<AccessPage />} />
        <Route path="/access/:environmentId" element={<AccessPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/audit/:environmentId" element={<AuditPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:environmentId" element={<CustomerDetailPage />} />
        {/* The old drill-down tabs and the old contact address still land, on the module each became. */}
        <Route path="/customers/:environmentId/journeys/contacts/:contactId" element={<LegacyContactRedirect />} />
        <Route path="/customers/:environmentId/:tab" element={<LegacyTabRedirect />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/users" element={<Navigate to="/access" replace />} />
        {/* Section 13.4: "My access" lives under Account now; the old address still lands there. */}
        <Route path="/my-access" element={<Navigate to="/account#access" replace />} />
        <Route path="/platform" element={<PlatformPage />} />
        {/* The documentation library: every customer-facing document under one navigation, release notes included. */}
        <Route path="/docs" element={<DocsLayout />}>
          <Route index element={<DocsIndexPage />} />
          <Route path="release-notes" element={<ReleaseNotesPage />} />
          <Route path=":slug" element={<DocPage />} />
        </Route>
        <Route path="/release-notes" element={<KeepHash to="/docs/release-notes" />} />
        <Route path="/data-handling" element={<Navigate to="/docs/what-leaves-your-account" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
